// eslint-disable-next-line no-unused-vars
import React, { forwardRef, useRef } from 'react';
import { oneOfType, object, bool, node, number, string } from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Info } from 'lucide-react';
import uniqueId from 'lodash/uniqueId';
import styled from '@emotion/styled';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';

import { UserContext } from 'Contexts/UserContext';
import useNumberUtils from 'Hooks/useNumberUtils';

const StyledContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: flex-end;
  text-transform: capitalize;
  font-size: 1rem;
  line-height: 1rem;

  .oldPrice {
    text-decoration: line-through;
    color: ${ props => props.theme.colors.gray };
    font-size: 1.25em;
    line-height: 1.2em;
  }

  .savings {
    font-size: 0.875rem;
    line-height: 1.125rem;
    color: #999999;
  }

  .currentPriceContainer {
    display: flex;
    align-items: center;

    .tooltip {
      margin-left: 5px;
    }
  }

  .priceInfo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }

  .currentPrice {
    font-size: 1.5em;
    line-height: 1.25em;
    color: ${ props => props.theme.colors.default };
  }

  .vatInfo {
    margin-left: 4px;
    text-transform: none;
    color: #999;
    font-size: 14px;
    font-weight: 400;
    margin: 4px 4px 0 8px;
  }

  @media screen and (max-width: 768px) {
    .react-tooltip {
      width: 100%;
      text-align: center;
    }
  }
`;

const Price = forwardRef((props, ref) => {
  const {
    price,
    promoPrice,
    withTooltip = false,
    tooltipAlign = 'top',
    tooltipContent = null,
    discountAmount = null,
    withVat,
    displayVatInfo = false,
  } = props;
  const { t } = useTranslation();
  const userCurrency = useContextSelector(UserContext, ({ data }) => data?.defaultCurrency);
  const currency = props.currency || userCurrency || 'RON';
  const { formatNumber } = useNumberUtils();

  const { current: oldPrice } = useRef(formatNumber(price, { maximumFractionDigits: 2 }));
  const { current: currentPrice } = useRef(formatNumber(promoPrice, { maximumFractionDigits: 2 }));
  const hasDiscount = oldPrice !== currentPrice;
  const uid = uniqueId();

  return (
    <StyledContainer ref={ ref } className="price">
      { (
        price !== undefined
        && hasDiscount
        && Math.round(promoPrice) < Math.round(price)
        && promoPrice !== 0
      ) && (
        <span className="oldPrice">
          { formatNumber(price, {
            maximumFractionDigits: 2,
            currency
          }) }
        </span>
      ) }
      { discountAmount !== null && (
        <p className="savings">
          {
            t(
              'products.youSave',
              {
                amount: formatNumber(discountAmount, { currency, maximumFractionDigits: 2 })
              }
            )
          }
        </p>
      ) }
      <div className="currentPriceContainer">
        <div className="priceInfo">
          <span className={ `currentPrice ${ promoPrice === 0 ? 'free' : '' }` }>
            { promoPrice === 0
              ? t('product.free')
              : formatNumber(promoPrice, {
                maximumFractionDigits: 2,
                currency
              })
            }
          </span>
          { displayVatInfo && <span className="vatInfo">{ `${withVat ? t('common.withVat') : t('common.withoutVat')}` }</span> }
        </div>
        { !!(withTooltip && tooltipContent && promoPrice !== 0) && (
          <div>
            <span className="tooltip" id={ `price-${ uid }` }><Info size={ 18 } /></span>
            <ReactTooltip
              multiline
              place={ tooltipAlign }
              className="price"
              anchorId={ `price-${ uid }` }
              variant="light"
              offset={ 15 }
              content={ tooltipContent }
            />
          </div>
        ) }
      </div>
    </StyledContainer>
  );
});

Price.propTypes = {
  price: oneOfType([number, string]),
  promoPrice: oneOfType([number, string]),
  currency: string,
  withTooltip: bool,
  tooltipAlign: string,
  discountAmount: number,
  tooltipContent: oneOfType([node, string, object]),
  withVat: bool,
  displayVatInfo: bool,
};

Price.displayName = 'PriceComponent';

export default Price;
